import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Heart, MapPin, MessageCircle, Share2, ShieldCheck, GitCompare, ShoppingCart, Star } from "lucide-react";
import { getProductDetailPath, getSellerDetailPath, replaceCanonicalPath, SendEnquiryModal, ProductTieredPricingBlock, normalizeTieredPricing } from "@b2b/ui-components";
import { productService, favoritesService, authService, sellerService } from "@b2b/services";
import type { FavoriteProduct } from "@b2b/services";
import { useAuth } from "../../auth/Authprovider";
import { buildLoginReturnState } from "../../auth/hooks/usePostLoginRedirect";
import { BizcoverIndiaFigmaHeader } from "../../components/marketing/BizcoverIndiaFigmaHeader";
import { BizcoverIndiaFigmaFooter } from "../../components/marketing/BizcoverIndiaFigmaFooter";
import {
  buildProductTradeSpecRows,
  formatProductUsdPrice,
  getProductUnitLabel,
} from "../../utils/productMapper";

const PLACEHOLDER_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect fill='%23f1f3f5' width='200' height='200'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='system-ui' font-size='11' fill='%23637381'%3ENo Image Preview%3C/text%3E%3C/svg%3E";

const PACK_SIZES = ["60 ML", "120 ML", "220 ML", "250 ML", "300 ML", "500 ML"] as const;

function normalizeImageList(product: any): string[] {
  const list: string[] = [];
  const push = (u: unknown) => {
    if (!u) return;
    if (typeof u === "string" && u.trim()) list.push(u.trim());
    else if (typeof u === "object" && u !== null && "url" in (u as any) && String((u as any).url).trim()) {
      list.push(String((u as any).url).trim());
    }
  };
  push(product?.thumbnailImage);
  const imgs = product?.images;
  if (Array.isArray(imgs)) imgs.forEach(push);
  const uniq: string[] = [];
  const seen = new Set<string>();
  for (const u of list) {
    if (seen.has(u)) continue;
    seen.add(u);
    uniq.push(u);
  }
  return uniq;
}

function pickDefaultPack(moqUnit?: string) {
  if (!moqUnit) return "250 ML";
  const s = String(moqUnit).trim();
  if (PACK_SIZES.includes(s as (typeof PACK_SIZES)[number])) return s;
  return "250 ML";
}

function formatDescriptionWithImageUrls(description: string): string {
  const raw = String(description || "").trim();
  if (!raw) return "";

  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(raw);
  const normalized = hasHtmlTags ? raw : raw.replace(/\n/g, "<br/>");

  const urlRegex = /(^|[\s>(])(https?:\/\/[^\s<>"']+)(?=$|[\s<)])/gi;
  const imageUrlRegex =
    /\.(png|jpe?g|gif|webp|svg|bmp|avif)(\?[^<>"'\s]*)?$/i;

  return normalized.replace(urlRegex, (match, prefix, url) => {
    if (!imageUrlRegex.test(url)) {
      return `${prefix}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    }
    return `${prefix}<img src="${url}" alt="Description image" loading="lazy" style="max-width:100%;height:auto;border-radius:8px;margin:8px 0;" />`;
  });
}

export default function BizcoverIndiaProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userRole, isAuthenticated } = useAuth();

  const [searchQuery, setSearchQuery] = useState("");
  const [footerEmail, setFooterEmail] = useState("");

  const [product, setProduct] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [imageIndex, setImageIndex] = useState(0);
  const [selectedPack, setSelectedPack] = useState<string>("250 ML");
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  const [isFavorited, setIsFavorited] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [relevanceProducts, setRelevanceProducts] = useState<any[]>([]);
  const [relevanceLoading, setRelevanceLoading] = useState(false);
  const relevanceScrollRef = useRef<HTMLDivElement | null>(null);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [favoriteLoadingIds, setFavoriteLoadingIds] = useState<Set<string>>(new Set());
  const [detailTab, setDetailTab] = useState<"description" | "additional">("description");

  const isBuyer = userRole === "buyer";

  const headerAnchorRef = useRef<HTMLDivElement | null>(null);
  const [stickyTop, setStickyTop] = useState(96);

  const galleryImgRef = useRef<HTMLImageElement | null>(null);
  const galleryWrapRef = useRef<HTMLDivElement | null>(null);
  const ZOOM_LENS = 180;
  const ZOOM_PREVIEW = 420;
  const ZOOM_RATIO = ZOOM_PREVIEW / ZOOM_LENS;
  const [zoom, setZoom] = useState({
    visible: false,
    x: 0,
    y: 0,
    imgW: 0,
    imgH: 0,
    offX: 0,
    offY: 0,
  });

  const handleZoomMove = (e: React.MouseEvent<HTMLImageElement>) => {
    const img = galleryImgRef.current;
    const wrap = galleryWrapRef.current;
    if (!img || !wrap) return;
    const ir = img.getBoundingClientRect();
    const wr = wrap.getBoundingClientRect();
    setZoom({
      visible: true,
      x: e.clientX - ir.left,
      y: e.clientY - ir.top,
      imgW: ir.width,
      imgH: ir.height,
      offX: ir.left - wr.left,
      offY: ir.top - wr.top,
    });
  };

  const handleZoomLeave = () => setZoom((s) => ({ ...s, visible: false }));

  useLayoutEffect(() => {
    const measureStickyTop = () => {
      const headerEl =
        headerAnchorRef.current?.querySelector("header") ??
        headerAnchorRef.current;
      const height = headerEl?.getBoundingClientRect().height ?? 0;
      setStickyTop(Math.round(height + 16));
    };

    measureStickyTop();
    window.addEventListener("resize", measureStickyTop);

    const headerEl =
      headerAnchorRef.current?.querySelector("header") ??
      headerAnchorRef.current;
    const observer =
      headerEl && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measureStickyTop)
        : null;
    if (headerEl && observer) observer.observe(headerEl);

    return () => {
      window.removeEventListener("resize", measureStickyTop);
      observer?.disconnect();
    };
  }, [loading]);

  const lensX = Math.max(0, Math.min(zoom.x - ZOOM_LENS / 2, Math.max(0, zoom.imgW - ZOOM_LENS)));
  const lensY = Math.max(0, Math.min(zoom.y - ZOOM_LENS / 2, Math.max(0, zoom.imgH - ZOOM_LENS)));

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSearch = () => {
    const t = searchQuery.trim();
    navigate(t ? `/search/${encodeURIComponent(t)}` : "/search");
  };

  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!id) {
        setError("Product ID is required");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await productService.getProductById(id);
        let productData: any = null;
        if (response?.product) productData = response.product;
        else if ((response as any)?.data?.product) productData = (response as any).data.product;
        else if ((response as any)?.data) productData = (response as any).data;
        else productData = response;
        if (!active) return;
        setProduct(productData);
        setError(null);
        setSelectedPack(pickDefaultPack(productData?.moqUnit));
        setImageIndex(0);

        // Silent URL canonicalize (no React Router navigate → no remount/refetch flicker).
        const canonicalSlug = String(productData?.slug || "").trim();
        if (canonicalSlug && id && id !== canonicalSlug) {
          replaceCanonicalPath(
            getProductDetailPath({ id: productData.id, slug: canonicalSlug }),
          );
        }
      } catch (e: any) {
        if (!active) return;
        setError(e?.message || "Failed to load product");
        setProduct(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    void run();
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    const checkFav = async () => {
      const productId = String(product?.id || "").trim();
      if (!productId || !isBuyer) {
        setIsFavorited(false);
        return;
      }
      try {
        const response = await favoritesService.getFavorites();
        const favorites = response.favorites ?? [];
        setIsFavorited(favorites.some((f: FavoriteProduct) => f.id === productId));
      } catch {
        setIsFavorited(false);
      }
    };
    void checkFav();
  }, [product?.id, isBuyer]);

  useEffect(() => {
    if (!isBuyer) {
      setFavoriteIds(new Set());
      return;
    }
    let active = true;
    favoritesService
      .getFavorites()
      .then((response) => {
        if (!active) return;
        const favorites = Array.isArray(response?.favorites) ? response.favorites : [];
        setFavoriteIds(new Set(favorites.map((favorite: any) => String(favorite.id))));
      })
      .catch((err) => {
        console.error("Failed to fetch favorites:", err);
      });
    return () => {
      active = false;
    };
  }, [isBuyer]);

  const images = useMemo(() => (product ? normalizeImageList(product) : []), [product]);
  const mainImage = images[imageIndex] || PLACEHOLDER_IMG;

  const currency = String(product?.currency || "USD").toUpperCase();
  const basePrice = Number(product?.price ?? 0) || 0;
  const discountPct = product?.discount != null ? Number(product.discount) : 0;
  const hasDiscount = discountPct > 0;
  const discounted = hasDiscount ? basePrice * (1 - discountPct / 100) : basePrice;

  const endDate = useMemo(() => {
    if (!product?.discountEndDate) return null;
    const d = new Date(product.discountEndDate);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [product?.discountEndDate]);

  const [timeLeft, setTimeLeft] = useState<{
    d: number;
    h: number;
    m: number;
    s: number;
  } | null>(null);

  useEffect(() => {
    if (!endDate) {
      setTimeLeft(null);
      return;
    }
    const tick = () => {
      const now = Date.now();
      const diff = endDate.getTime() - now;
      if (diff <= 0) {
        setTimeLeft(null);
        return;
      }
      const s = Math.floor(diff / 1000);
      setTimeLeft({
        d: Math.floor(s / 86400),
        h: Math.floor((s % 86400) / 3600),
        m: Math.floor((s % 3600) / 60),
        s: s % 60,
      });
    };
    tick();
    const t = window.setInterval(tick, 1000);
    return () => window.clearInterval(t);
  }, [endDate]);

  const stock = product?.stock != null ? Number(product.stock) : product?.inventory != null ? Number(product.inventory) : null;

  const stockPercent = useMemo(() => {
    if (stock == null || !Number.isFinite(stock)) return 72;
    const capped = Math.min(100, Math.max(0, stock));
    return Math.round((capped / 200) * 100);
  }, [stock]);

  const categoryLabel = useMemo(() => {
    if (!product) return "";
    const a =
      product?.categoryRelation?.name ||
      (typeof product?.category === "string" ? product.category : product?.category?.name) ||
      product?.categoryName ||
      product?.subcategory;
    return String(a || "").trim();
  }, [product]);

  const breadMiddle = categoryLabel || "Category";
  const descriptionContent = useMemo(() => {
    const candidate =
      product?.description ||
      product?.longDescription ||
      product?.productDescription ||
      product?.details ||
      product?.overview ||
      "";
    return String(candidate || "").trim();
  }, [product]);

  const descriptionHasHtml = /<[^>]+>/.test(descriptionContent);
  const descriptionRenderedHtml = useMemo(
    () => formatDescriptionWithImageUrls(descriptionContent),
    [descriptionContent]
  );

  const featureList = useMemo(() => {
    const raw =
      product?.keyFeatures ||
      product?.features ||
      product?.benefits ||
      product?.highlights ||
      [];

    if (Array.isArray(raw)) {
      return raw
        .map((item) => String(item || "").trim())
        .filter(Boolean)
        .slice(0, 8);
    }

    if (typeof raw === "string" && raw.trim()) {
      return raw
        .split(/\n|,|•|-/g)
        .map((item) => item.trim())
        .filter((item) => item.length > 2)
        .slice(0, 8);
    }

    return [];
  }, [product]);

  const unitLabel = useMemo(() => getProductUnitLabel(product), [product]);
  const hasTieredPricing = useMemo(
    () => normalizeTieredPricing(product?.tieredPricing).length > 0,
    [product?.tieredPricing],
  );

  const tradeSpecRows = useMemo(() => buildProductTradeSpecRows(product), [product]);

  const additionalInfoRows = useMemo(() => {
    const extra = [
      { label: "SKU", value: String(product?.sku || product?.id || "").trim() },
      { label: "Model", value: String(product?.model || "").trim() },
      { label: "Category", value: categoryLabel },
      { label: "Grade", value: String(product?.grade || "").trim() },
      { label: "HS Code", value: String(product?.hsCode || "").trim() },
    ].filter((row) => row.value);
    return [...tradeSpecRows.filter((row) => row.value), ...extra];
  }, [product, categoryLabel, tradeSpecRows]);
  const productCategoryId = useMemo(() => {
    const raw = product?.categoryId || product?.category?.id || product?.categoryRelation?.id;
    return raw ? String(raw) : "";
  }, [product]);

  const sellerName = useMemo(() => {
    if (!product) return "Seller";
    return (
      product?.company?.name ||
      (product?.assignedSeller
        ? [product.assignedSeller.firstName, product.assignedSeller.lastName].filter(Boolean).join(" ")
        : null) ||
      "Seller"
    );
  }, [product]);

  const getSellerId = (): string | null => {
    if (!product) return null;
    if (product.assignedSellerId) return product.assignedSellerId;
    if (product.assignedSeller?.id) return product.assignedSeller.id;
    if (product.company?.userProfiles && Array.isArray(product.company.userProfiles)) {
      const seller = product.company.userProfiles.find(
        (user: any) =>
          user.role === "SELLER" || user.role === "BUYER_SELLER" || user.sellerStatus === "APPROVED"
      );
      if (seller?.id) return seller.id;
    }
    return null;
  };

  const sellerId = getSellerId();
  const sellerProfileUrl =
    sellerId != null ? getSellerDetailPath({ id: sellerId, slug: (product as any)?.company?.slug }) : null;

  const sellerSummary = useMemo(() => {
    const company = product?.company || {};
    const assigned = product?.assignedSeller || {};
    const logo = String(company.logo || assigned.profileImage || "").trim();
    const country = String(company.country || assigned.country || "").trim();
    const city = String(company.city || assigned.city || "").trim();
    const location = [city, country].filter(Boolean).join(", ");
    const foundedYear = Number(company.foundedYear || 0);
    const yearsInBusiness =
      foundedYear > 1900 && foundedYear <= new Date().getFullYear()
        ? Math.max(1, new Date().getFullYear() - foundedYear)
        : null;
    const isVerified =
      Boolean(company.isVerified) ||
      Boolean(assigned.isVerified) ||
      String(assigned.sellerStatus || "").toUpperCase() === "APPROVED";
    const initial = String(sellerName || "S").trim().charAt(0).toUpperCase() || "S";
    return { logo, location, yearsInBusiness, isVerified, initial };
  }, [product, sellerName]);

  const openChat = () => {
    if (!sellerId) {
      showToast("error", "Seller information unavailable");
      return;
    }
    const search = `?sellerId=${encodeURIComponent(sellerId)}`;
    if (!isAuthenticated) {
      navigate("/login", {
        state: buildLoginReturnState("/chat", search),
      });
      return;
    }
    navigate(`/chat${search}`);
  };

  const openSellerProfile = () => {
    if (!sellerProfileUrl) return;
    if (!isAuthenticated) {
      navigate("/login", { state: buildLoginReturnState(sellerProfileUrl) });
      return;
    }
    navigate(sellerProfileUrl);
  };

  const getCurrentUserForEnquiry = useCallback(async () => {
    const me = await authService.getMe();
    const u = me?.user;
    const name =
      u?.firstName && u?.lastName
        ? `${u.firstName} ${u.lastName}`.trim()
        : u?.firstName || u?.lastName || u?.email || "";
    return {
      buyerName: name || "—",
      buyerEmail: u?.email || "",
    };
  }, []);

  const handleSendEnquirySubmit = async (payload: {
    productId: string;
    subject: string;
    message: string;
    buyerName: string;
    buyerEmail: string;
    buyerPhone?: string;
  }) => {
    await sellerService.createInquiry(payload);
    showToast("success", "Enquiry sent");
  };

  const handleFavorite = async () => {
    if (!id || !isBuyer) {
      navigate("/login");
      return;
    }
    if (favLoading) return;
    setFavLoading(true);
    const next = !isFavorited;
    setIsFavorited(next);
    try {
      if (next) await favoritesService.addFavorite(id);
      else await favoritesService.removeFavorite(id);
      const response = await favoritesService.getFavorites();
      const favorites = response.favorites ?? [];
      setIsFavorited(favorites.some((f: FavoriteProduct) => f.id === id));
    } catch (e: any) {
      setIsFavorited(!next);
      showToast("error", e?.response?.data?.message || "Failed to update wishlist");
    } finally {
      setFavLoading(false);
    }
  };

  const openEnquiry = () => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    setEnquiryOpen(true);
  };

  useEffect(() => {
    const fetchRelevanceProducts = async () => {
      if (!productCategoryId) {
        setRelevanceProducts([]);
        return;
      }
      try {
        setRelevanceLoading(true);
        const response = await productService.getProductsByCategory(productCategoryId, {
          page: 1,
          limit: 14,
        });

        let products: any[] = [];
        if (response?.products && Array.isArray(response.products)) products = response.products;
        else if ((response as any)?.data?.products && Array.isArray((response as any).data.products)) {
          products = (response as any).data.products;
        } else if (Array.isArray(response)) {
          products = response;
        }

        const next = products
          .filter((p) => p?.id && p.id !== product?.id)
          .map((p) => {
            const base = Number(p?.price ?? p?.priceMin ?? 0) || 0;
            const pct = p?.discount != null ? Number(p.discount) : 0;
            const hasPct = Number.isFinite(pct) && pct > 0;
            const finalPrice = hasPct ? base * (1 - pct / 100) : base;
            const image =
              p?.thumbnailImage ||
              (Array.isArray(p?.images) && p.images.length > 0 ? p.images[0] : "") ||
              PLACEHOLDER_IMG;

            const unitLabel = getProductUnitLabel(p);
            return {
              id: String(p.id),
              name: p?.name || p?.productName || "Product",
              image,
              rating: Number(p?.rating ?? 4.5),
              price: finalPrice,
              originalPrice: hasPct ? base : null,
              discountPct: hasPct ? Math.round(pct) : 0,
              unitLabel: unitLabel || undefined,
            };
          })
          .slice(0, 12);

        setRelevanceProducts(next);
      } catch {
        setRelevanceProducts([]);
      } finally {
        setRelevanceLoading(false);
      }
    };
    void fetchRelevanceProducts();
  }, [productCategoryId, product?.id]);

  const scrollRelevance = (dir: "left" | "right") => {
    const el = relevanceScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -340 : 340, behavior: "smooth" });
  };

  const handleToggleRelevanceFavorite = async (productId: string, isFavorited: boolean) => {
    if (!isBuyer) {
      navigate("/login");
      return;
    }
    if (favoriteLoadingIds.has(productId)) return;
    setFavoriteLoadingIds((prev) => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
    const previous = new Set(favoriteIds);
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (isFavorited) next.delete(productId);
      else next.add(productId);
      return next;
    });
    try {
      if (isFavorited) {
        await favoritesService.removeFavorite(productId);
      } else {
        await favoritesService.addFavorite(productId);
      }
    } catch (err) {
      console.error("Failed to update favorites:", err);
      setFavoriteIds(previous);
    } finally {
      setFavoriteLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f6f8]">
        <BizcoverIndiaFigmaHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSearchSubmit={handleSearch}
          onExploreCategoriesClick={() => navigate("/categories")}
        />
        <div className="flex w-full animate-fade-in items-center justify-center gap-3 px-4 py-24 text-[#637381] lg:px-36">
          <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-[#FF6A00]" />
          <span className="font-medium">Loading product…</span>
        </div>
        <BizcoverIndiaFigmaFooter email={footerEmail} onEmailChange={setFooterEmail} onSubscribe={() => {}} />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-[#f4f6f8]">
        <BizcoverIndiaFigmaHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSearchSubmit={handleSearch}
          onExploreCategoriesClick={() => navigate("/categories")}
        />
        <div className="w-full px-4 py-16 text-center text-[#637381] lg:px-36">
          <p className="text-lg text-[#dc2626]">{error || "Product not found"}</p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 text-sm font-semibold text-[#FF6A00] shadow transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#FF6A00]/20 active:translate-y-0 active:scale-95"
          >
            <ChevronLeft className="h-4 w-4" />
            Go back
          </button>
        </div>
        <BizcoverIndiaFigmaFooter email={footerEmail} onEmailChange={setFooterEmail} onSubscribe={() => {}} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f6f8]">
      {toast && (
        <div
          className={`fixed right-4 top-4 z-[100] animate-fade-up rounded-lg px-4 py-3 text-sm font-medium text-white shadow-xl ring-1 ring-black/5 ${
            toast.type === "success" ? "bg-emerald-600" : "bg-[#FF4400]"
          }`}
        >
          {toast.message}
        </div>
      )}

      <div ref={headerAnchorRef}>
        <BizcoverIndiaFigmaHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSearchSubmit={handleSearch}
          onExploreCategoriesClick={() => navigate("/categories")}
        />
      </div>

      <main className="w-full px-4 py-6 sm:py-8 lg:px-36">
        <nav className="mb-6 flex flex-wrap items-center gap-2 text-[13px] text-[#637381] animate-fade-in">
          <Link to="/bizcoverindia-home-updated" className="transition-colors duration-200 hover:text-[#FF6A00]">
            Home
          </Link>
          <span className="text-[#c9d0d8]">/</span>
          <span className="text-[#212529]">{breadMiddle}</span>
          <span className="text-[#c9d0d8]">/</span>
          <span className="font-medium text-[#212529]">Product detail</span>
        </nav>

        <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
          {/* Sticky gallery: stays fixed while the right column scrolls; unsticks when the row ends */}
          <section
            className="relative z-10 animate-fade-up rounded-2xl bg-white p-4 shadow-sm transition-shadow duration-300 hover:shadow-md sm:p-5 lg:sticky lg:self-start"
            style={{ top: stickyTop }}
          >
            <div className="flex gap-3 sm:gap-4">
              <div className="hidden w-[64px] shrink-0 flex-col gap-2 sm:flex">
                {images.length === 0
                  ? [0, 1, 2, 3, 4, 5].map((i) => (
                      <button
                        type="button"
                        key={i}
                        onClick={() => setImageIndex(0)}
                        className="h-14 w-14 overflow-hidden rounded-lg border border-[#e6ebf0] bg-[#f7f9fc] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#FF6A00] hover:shadow-md"
                      >
                        <img src={PLACEHOLDER_IMG} alt="" className="h-full w-full object-cover transition-transform duration-300 hover:scale-110" />
                      </button>
                    ))
                  : images.slice(0, 6).map((src, i) => (
                      <button
                        type="button"
                        key={src + i}
                        onClick={() => setImageIndex(i)}
                        onMouseEnter={() => setImageIndex(i)}
                        className={`h-14 w-14 overflow-hidden rounded-lg border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                          i === imageIndex
                            ? "scale-[1.04] border-[#FF6A00] shadow-md ring-2 ring-[#FF6A00]/40"
                            : "border-[#e6ebf0] hover:border-[#FFB070]"
                        }`}
                      >
                        <img src={src} alt="" className="h-full w-full object-cover transition-transform duration-300 hover:scale-110" />
                      </button>
                    ))}
              </div>

              <div ref={galleryWrapRef} className="relative flex-1">
                <div className="group relative flex min-h-[280px] w-full items-center justify-center overflow-hidden rounded-2xl bg-[#f1f3f5] p-4 sm:min-h-[340px] lg:min-h-[420px]">
                  <img
                    ref={galleryImgRef}
                    src={mainImage}
                    alt={product.name}
                    className="max-h-[500px] w-auto max-w-full select-none object-contain"
                    draggable={false}
                    onMouseEnter={handleZoomMove}
                    onMouseMove={handleZoomMove}
                    onMouseLeave={handleZoomLeave}
                  />

                  {zoom.visible && zoom.imgW > ZOOM_LENS && zoom.imgH > ZOOM_LENS && (
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute z-[3] hidden border border-[#FF6A00] bg-[#FF6A00]/15 mix-blend-multiply lg:block"
                      style={{
                        width: ZOOM_LENS,
                        height: ZOOM_LENS,
                        left: zoom.offX + lensX,
                        top: zoom.offY + lensY,
                      }}
                    />
                  )}

                  {images.length > 1 && (
                    <div className="pointer-events-none absolute bottom-3 right-3 z-[3] rounded-full bg-black/55 px-2.5 py-0.5 text-[11px] font-medium tabular-nums text-white">
                      {imageIndex + 1} / {images.length}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setImageIndex((i) => (images.length ? (i - 1 + images.length) % images.length : 0))}
                    className="absolute left-3 top-1/2 z-[2] flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#212529] opacity-100 shadow transition-all duration-200 hover:scale-110 hover:bg-white hover:text-[#FF6A00] hover:shadow-lg active:scale-95 lg:opacity-0 lg:group-hover:opacity-100"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageIndex((i) => (images.length ? (i + 1) % images.length : 0))}
                    className="absolute right-3 top-1/2 z-[2] flex h-9 w-9 -translate-y-1/2 rotate-180 items-center justify-center rounded-full bg-white/90 text-[#212529] opacity-100 shadow transition-all duration-200 hover:scale-110 hover:bg-white hover:text-[#FF6A00] hover:shadow-lg active:scale-95 lg:opacity-0 lg:group-hover:opacity-100"
                    aria-label="Next image"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                </div>

                {zoom.visible && zoom.imgW > 0 && zoom.imgH > 0 && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute top-0 z-50 hidden animate-fade-in overflow-hidden rounded-xl border border-[#FF6A00]/40 bg-white shadow-2xl ring-1 ring-black/5 lg:block"
                    style={{
                      left: "calc(100% + 16px)",
                      width: ZOOM_PREVIEW,
                      height: ZOOM_PREVIEW,
                      backgroundImage: `url("${mainImage}")`,
                      backgroundRepeat: "no-repeat",
                      backgroundColor: "#fff",
                      backgroundSize: `${zoom.imgW * ZOOM_RATIO}px ${zoom.imgH * ZOOM_RATIO}px`,
                      backgroundPosition: `-${lensX * ZOOM_RATIO}px -${lensY * ZOOM_RATIO}px`,
                    }}
                  />
                )}
              </div>
            </div>
          </section>

          <section className="relative z-0 animate-fade-up-1 rounded-2xl border border-[#e9eef4] bg-white p-5 shadow-sm transition-shadow duration-300 hover:shadow-md sm:p-6">
            {/* <div className="mb-1 inline-flex items-center gap-2 rounded bg-[#dcfce7] px-2 py-0.5 text-[10px] font-bold uppercase text-[#166534]">
              New
            </div>
            <p className="text-[12px] font-semibold text-[#2563eb]">NEW ARRIVAL</p> */}

            <div className="mt-3 flex items-start justify-between gap-3">
              <h1 className="text-[22px] font-bold leading-7 tracking-tight text-[#111827] sm:text-[28px] sm:leading-[34px]">
                {product.name}
              </h1>
              {userRole === "buyer" && (
              <button
                type="button"
                onClick={handleFavorite}
                disabled={favLoading}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e6ebf0] text-[#FF6A00] transition-all duration-200 hover:scale-105 hover:border-[#FF6A00] hover:bg-[#fff7ed] hover:shadow-md active:scale-90 disabled:opacity-60"
                aria-label="Wishlist"
              >
                <Heart className={`h-5 w-5 transition-transform ${isFavorited ? "fill-[#FF6A00] animate-pop" : ""}`} />
              </button>
              )}
            </div>

            {hasTieredPricing ? (
              <div className="mt-4">
                {basePrice > 0 && (
                  <div>
                    <p className="text-[13px] font-medium text-[#637381]">Unit price (USD)</p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-[24px] font-extrabold tracking-tight tabular-nums text-[#FF4400]">
                        {formatProductUsdPrice(discounted)}
                      </span>
                      {unitLabel ? (
                        <span className="rounded bg-[#fff7ed] px-2 py-0.5 text-[13px] font-semibold text-[#b45309]">
                          Per {unitLabel}
                        </span>
                      ) : null}
                      {hasDiscount && (
                        <>
                          <span className="text-[13px] tabular-nums font-medium text-[#94a3b8] line-through">
                            {formatProductUsdPrice(basePrice)}
                          </span>
                          <span className="rounded bg-[#FF4400] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                            {Math.round(discountPct)}% OFF
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}
                <div className="mt-3">
                  <ProductTieredPricingBlock
                    product={product}
                    discountPct={hasDiscount ? discountPct : 0}
                    variant="bizcover"
                    hideSummary
                  />
                </div>
              </div>
            ) : (
            <div className="mt-4">
              <p className="text-[13px] font-medium text-[#637381]">Price in USD</p>
              <div className="mt-1 flex flex-wrap items-end gap-2">
                <span className="text-[32px] font-extrabold tracking-tight tabular-nums text-[#FF4400]">
                  {formatProductUsdPrice(discounted)}
                </span>
                {hasDiscount && (
                  <>
                    <span className="text-[15px] tabular-nums text-[#919eab] line-through">
                      {formatProductUsdPrice(basePrice)}
                    </span>
                    <span className="animate-pulse-ring rounded bg-[#FF4400] px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">
                      {Math.round(discountPct)}% OFF
                    </span>
                  </>
                )}
              </div>
              {unitLabel ? (
                <p className="mt-1 text-[14px] text-[#4b5563]">
                  <span className="text-[#637381]">Per </span>
                  {unitLabel}
                </p>
              ) : null}
            </div>
            )}

            <div className="mt-5 rounded-xl border border-[#e9eef4] bg-gradient-to-br from-white to-[#fff7ed]/60 p-3 transition-all duration-200 hover:border-[#FFB070] hover:shadow-md">
              <div className="flex items-start gap-3">
                {sellerProfileUrl ? (
                  <button
                    type="button"
                    onClick={openSellerProfile}
                    className="group/avatar relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#e6ebf0] bg-white text-sm font-bold text-[#FF6A00] shadow-sm transition-transform duration-200 hover:scale-105"
                    aria-label={`Visit ${sellerName}`}
                  >
                    {sellerSummary.logo ? (
                      <img
                        src={sellerSummary.logo}
                        alt={sellerName}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover/avatar:scale-110"
                      />
                    ) : (
                      <span>{sellerSummary.initial}</span>
                    )}
                  </button>
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#e6ebf0] bg-white text-sm font-bold text-[#FF6A00] shadow-sm">
                    {sellerSummary.logo ? (
                      <img src={sellerSummary.logo} alt={sellerName} className="h-full w-full object-cover" />
                    ) : (
                      <span>{sellerSummary.initial}</span>
                    )}
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {sellerProfileUrl ? (
                      <button
                        type="button"
                        onClick={openSellerProfile}
                        className="truncate text-left text-[14px] font-bold text-[#1f2937] transition-colors duration-200 hover:text-[#FF6A00]"
                      >
                        {sellerName}
                      </button>
                    ) : (
                      <span className="truncate text-[14px] font-bold text-[#1f2937]">{sellerName}</span>
                    )}
                    {sellerSummary.isVerified && (
                      <span className="inline-flex items-center gap-0.5 rounded bg-[#FF6A00]/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#FF6A00]">
                        <ShieldCheck className="h-3 w-3" />
                        Verified
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#6b7280]">
                    {sellerSummary.location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {sellerSummary.location}
                      </span>
                    )}
                    {sellerSummary.yearsInBusiness != null && (
                      <span className="inline-flex items-center gap-1">
                        <span className="h-1 w-1 rounded-full bg-[#d1d5db]" />
                        {sellerSummary.yearsInBusiness} {sellerSummary.yearsInBusiness === 1 ? "yr" : "yrs"} in business
                      </span>
                    )}
                  </div>
                </div>

                {sellerProfileUrl && (
                  <button
                    type="button"
                    onClick={openSellerProfile}
                    className="shrink-0 self-start text-[11px] font-semibold text-[#FF6A00] transition-all duration-200 hover:translate-x-0.5 hover:underline"
                  >
                    View →
                  </button>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={openEnquiry}
                className="rounded-xl bg-gradient-to-r from-[#FF6A00] to-[#FF4400] py-3 text-sm font-bold uppercase tracking-wide text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#FF6A00]/30 active:translate-y-0 active:scale-[0.98]"
              >
                Send Enquiry
              </button>
              <button
                type="button"
                onClick={openChat}
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-[#FF6A00] bg-white py-[10px] text-sm font-bold uppercase tracking-wide text-[#FF6A00] shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#fff7ed] hover:shadow-lg hover:shadow-[#FF6A00]/20 active:translate-y-0 active:scale-[0.98]"
              >
                <MessageCircle className="h-4 w-4" />
                Chat Now
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-[#637381]">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 transition-colors duration-200 hover:text-[#FF6A00]"
                  onClick={async () => {
                    const url = window.location.href;
                    if (navigator.share) {
                      try {
                        await navigator.share({ title: product.name, url });
                      } catch {
                        void navigator.clipboard.writeText(url);
                        showToast("success", "Link copied");
                      }
                    } else {
                      void navigator.clipboard.writeText(url);
                      showToast("success", "Link copied");
                    }
                  }}
                >
                  <Share2 className="h-4 w-4" />
                  Share
                </button>
                {/* <span className="inline-flex items-center gap-1">
                  <GitCompare className="h-4 w-4" />
                  Compare
                </span> */}
              </div>
            </div>

            <div className="mt-6 space-y-2 border-t border-[#eef1f4] pt-4 text-sm text-[#4b5563]">
              {tradeSpecRows.map((spec) => (
                <p key={spec.label}>
                  <span className="font-semibold text-[#111827]">{spec.label} :</span>
                  {spec.value ? ` ${spec.value}` : ""}
                </p>
              ))}
            </div>
          </section>
        </div>

        <section className="mt-8 animate-fade-up-2">
          <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-[#e5e7eb]">
            <button
              type="button"
              onClick={() => setDetailTab("description")}
              className={`rounded-t-lg px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-all duration-200 ${
                detailTab === "description"
                  ? "bg-gradient-to-r from-[#FF6A00] to-[#FF4400] text-white shadow-md"
                  : "text-[#637381] hover:bg-[#fff7ed] hover:text-[#FF6A00]"
              }`}
            >
              Description
            </button>
            <button
              type="button"
              onClick={() => setDetailTab("additional")}
              className={`rounded-t-lg px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-all duration-200 ${
                detailTab === "additional"
                  ? "bg-gradient-to-r from-[#FF6A00] to-[#FF4400] text-white shadow-md"
                  : "text-[#637381] hover:bg-[#fff7ed] hover:text-[#FF6A00]"
              }`}
            >
              Additional information
            </button>
          </div>

          <div className="animate-fade-in rounded-2xl border border-[#e5e7eb] bg-white p-4 transition-shadow duration-300 hover:shadow-md sm:p-6">
            {detailTab === "description" && (
              <div className="space-y-5">
                <h3 className="text-[20px] font-semibold text-[#212b36]">Descriptions</h3>
                {descriptionContent ? (
                  descriptionHasHtml ? (
                    <div
                      className="prose prose-sm max-w-none text-[#4b5563]"
                      dangerouslySetInnerHTML={{ __html: descriptionRenderedHtml }}
                    />
                  ) : (
                    <div
                      className="prose prose-sm max-w-none text-[#4b5563]"
                      dangerouslySetInnerHTML={{ __html: descriptionRenderedHtml }}
                    />
                  )
                ) : (
                  <p className="text-sm leading-7 text-[#4b5563]">
                    Product description is currently unavailable. Please contact the seller for detailed specs and
                    usage information.
                  </p>
                )}

                {featureList.length > 0 && (
                  <div className="grid gap-3 pt-1 sm:grid-cols-2">
                    {featureList.map((item, idx) => (
                      <div key={`${item}-${idx}`} className="flex items-start gap-2 text-sm text-[#334155]">
                        <span className="mt-[7px] inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-[#0f766e]" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {detailTab === "additional" && (
              <div className="space-y-4">
                <h3 className="text-[20px] font-semibold text-[#212b36]">Additional information</h3>
                {additionalInfoRows.length > 0 ? (
                  <div className="overflow-hidden rounded-xl border border-[#eef1f4]">
                    {additionalInfoRows.map((row, idx) => (
                      <div
                        key={row.label}
                        className={`grid grid-cols-[180px_1fr] gap-4 px-4 py-3 text-sm ${
                          idx !== additionalInfoRows.length - 1 ? "border-b border-[#eef1f4]" : ""
                        }`}
                      >
                        <span className="font-semibold text-[#1f2937]">{row.label}</span>
                        <span className="text-[#4b5563]">{row.value}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#637381]">No additional information available for this product.</p>
                )}
              </div>
            )}

          </div>
        </section>

        <section className="mt-10 animate-fade-up-3 rounded-2xl bg-white p-4 shadow-sm transition-shadow duration-300 hover:shadow-md sm:p-5">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-[28px] font-bold tracking-tight text-[#212b36]">Recommended Products</h2>
            {relevanceProducts.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => scrollRelevance("left")}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#F97316]/45 bg-[#fff7ed] text-[#F97316] transition-all duration-200 hover:scale-110 hover:border-[#F97316] hover:bg-[#F97316] hover:text-white active:scale-95"
                aria-label="Scroll recommended products left"
              >
                <ChevronLeft className="h-4 w-4" strokeWidth={2.25} />
              </button>
              <button
                type="button"
                onClick={() => scrollRelevance("right")}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#F97316]/45 bg-[#fff7ed] text-[#F97316] transition-all duration-200 hover:scale-110 hover:border-[#F97316] hover:bg-[#F97316] hover:text-white active:scale-95"
                aria-label="Scroll recommended products right"
              >
                <ChevronRight className="h-4 w-4" strokeWidth={2.25} />
              </button>
            </div>
            )}
          </div>

          {relevanceLoading ? (
            <div className="py-8 text-center text-sm text-[#637381]">Loading recommended products...</div>
          ) : relevanceProducts.length === 0 ? (
            <div className="py-8 text-center text-sm text-[#637381]">No recommended products found.</div>
          ) : (
            <div
              ref={relevanceScrollRef}
              className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {relevanceProducts.map((item) => (
                (() => {
                  const relevanceId = String(item.id);
                  const isRelevanceFavorited = favoriteIds.has(relevanceId);
                  const isRelevanceFavoriteLoading = favoriteLoadingIds.has(relevanceId);
                  return (
                <article
                  key={item.id}
                  className="group w-[170px] shrink-0 rounded-xl border border-[#e9eef4] bg-white p-2 transition-all duration-300 hover:-translate-y-1 hover:border-[#FFB070] hover:shadow-lg hover:shadow-[#FF6A00]/10"
                >
                  <button
                    type="button"
                    className="block w-full text-left"
                    onClick={() => navigate(getProductDetailPath(item))}
                  >
                    <div className="overflow-hidden rounded-lg bg-[#f1f3f5]">
                      <img
                        src={item.image || PLACEHOLDER_IMG}
                        alt={item.name}
                        className="h-[112px] w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src = PLACEHOLDER_IMG;
                        }}
                      />
                    </div>
                    <h3 className="mt-2 line-clamp-2 min-h-[34px] text-[11px] font-semibold text-[#1f2937] transition-colors duration-200 group-hover:text-[#FF6A00]">
                      {item.name}
                    </h3>
                  </button>



                  <div className="mt-2">
                    <div className="flex flex-wrap items-end gap-1.5">
                      <span className="text-[12px] font-bold tabular-nums text-[#FF4400]">{formatProductUsdPrice(item.price)}</span>
                      {item.originalPrice != null && (
                        <>
                          <span className="text-[10px] tabular-nums text-[#9ca3af] line-through">
                            {formatProductUsdPrice(item.originalPrice)}
                          </span>
                          <span className="text-[10px] font-semibold text-[#FF4400]">{item.discountPct}% OFF</span>
                        </>
                      )}
                    </div>
                    {item.unitLabel ? (
                      <p className="mt-0.5 text-[10px] text-[#637381]">
                        <span className="text-[#919eab]">Per </span>
                        {item.unitLabel}
                      </p>
                    ) : null}
                  </div>

                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      disabled={isRelevanceFavoriteLoading}
                      onClick={() => handleToggleRelevanceFavorite(relevanceId, isRelevanceFavorited)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#dfe3e8] text-[#94a3b8] transition-all duration-200 hover:scale-110 hover:border-[#FF6A00] hover:text-[#FF6A00] active:scale-90 disabled:opacity-60"
                      aria-label={isRelevanceFavorited ? "Remove from favorites" : "Add to favorites"}
                    >
                      <Heart className={`h-3.5 w-3.5 transition-transform ${isRelevanceFavorited ? "fill-[#FF6A00] text-[#FF6A00] animate-pop" : ""}`} />
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(getProductDetailPath(item))}
                      className="inline-flex flex-1 items-center justify-center rounded-full bg-gradient-to-r from-[#FF6A00] to-[#FF4400] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm transition-all duration-200 hover:shadow-md hover:shadow-[#FF6A00]/30 active:scale-95"
                    >
                      View Product
                    </button>
                  </div>
                </article>
                  );
                })()
              ))}
            </div>
          )}
        </section>
      </main>

      <BizcoverIndiaFigmaFooter
        email={footerEmail}
        onEmailChange={setFooterEmail}
        onSubscribe={() => {
          if (!footerEmail.trim()) return;
          showToast("success", "Thanks — subscription captured");
        }}
      />

      <SendEnquiryModal
        open={enquiryOpen}
        onOpenChange={setEnquiryOpen}
        product={{
          id: product.id,
          name: product.name,
          title: product.title,
          thumbnailImage: product.thumbnailImage,
          images: product.images,
          moq: product.moq,
          moqUnit: product.moqUnit,
        }}
        sellerName={sellerName}
        sellerProfileUrl={sellerProfileUrl}
        sellerCountry={product.company?.country}
        sellerYearsInBusiness={
          product.company?.foundedYear ? new Date().getFullYear() - product.company.foundedYear : null
        }
        getCurrentUser={getCurrentUserForEnquiry}
        onSubmit={handleSendEnquirySubmit}
      />
    </div>
  );
}
